import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { toastr } from 'react-redux-toastr';

import Loader from '../../../components/Common/Loader/Loader';
import getMarketingSettingsQuery from './getMarketingSettings.graphql';
import updateMarketingSettingsMutation from './updateMarketingSettings.graphql';

const FIELDS = [
  { key: 'gtmId', label: 'Google Tag Manager ID', placeholder: 'GTM-XXXXXXX',
    help: 'Container ID. If set, it loads on every page (manage GA4 / Ads / pixels inside GTM too).' },
  { key: 'ga4Id', label: 'Google Analytics 4 ID', placeholder: 'G-XXXXXXXXXX',
    help: 'Measurement ID. Tracks traffic + fires a "purchase" event on every booking.' },
  { key: 'metaPixelId', label: 'Meta (Facebook/Instagram) Pixel ID', placeholder: '123456789012345',
    help: 'Builds your retargeting audience + fires "Purchase" on booking for ad optimization.' },
  { key: 'googleAdsId', label: 'Google Ads Conversion ID', placeholder: 'AW-XXXXXXXXX',
    help: 'For Google Ads remarketing + conversion tracking.' },
  { key: 'googleAdsLabel', label: 'Google Ads Conversion Label', placeholder: 'abcDEF...',
    help: 'The label from your Google Ads "purchase" conversion action (used with the ID above).' },
];

const MarketingTracking = () => {
  const { loading, data, refetch } = useQuery(getMarketingSettingsQuery, { fetchPolicy: 'network-only' });
  const [save, { loading: saving }] = useMutation(updateMarketingSettingsMutation);
  const [form, setForm] = useState({});

  useEffect(() => {
    const d = data?.getMarketingSettings;
    if (d) {
      setForm({
        gtmId: d.gtmId || '', ga4Id: d.ga4Id || '', metaPixelId: d.metaPixelId || '',
        googleAdsId: d.googleAdsId || '', googleAdsLabel: d.googleAdsLabel || '',
        abandonedCartEnabled: d.abandonedCartEnabled === '1',
      });
    }
  }, [data]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const onSave = async () => {
    try {
      const res = await save({
        variables: {
          gtmId: form.gtmId || '', ga4Id: form.ga4Id || '', metaPixelId: form.metaPixelId || '',
          googleAdsId: form.googleAdsId || '', googleAdsLabel: form.googleAdsLabel || '',
          abandonedCartEnabled: form.abandonedCartEnabled ? '1' : '0',
        },
      });
      const out = res?.data?.updateMarketingSettings;
      if (out?.status === 200) {
        toastr.success('Saved', 'Marketing & tracking settings updated. Live on the next page load.');
        await refetch();
      } else {
        toastr.error('Error', out?.errorMessage || 'Could not save.');
      }
    } catch (e) {
      toastr.error('Error', e.message || 'Could not save.');
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 760 }}>
      <h1 className="siteAdminPrimaryHeading">Marketing &amp; Tracking</h1>
      <p style={{ color: '#5f6368' }}>
        Paste your IDs below. Each one is <b>injected automatically site-wide</b> as soon as you save —
        no code needed. Pixels build your retargeting audiences (so visitors see your ads across the web)
        and a conversion event fires on every booking. Leave a field empty to turn that one off.
      </p>

      <Loader type="page" show={loading}>
        {!loading && (
          <>
            {FIELDS.map(f => (
              <Form.Group key={f.key} style={{ marginBottom: 18 }}>
                <Form.Label style={{ fontWeight: 600 }}>{f.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={form[f.key] || ''}
                  placeholder={f.placeholder}
                  onChange={e => set(f.key, e.target.value)}
                />
                <Form.Text style={{ color: '#9AA0A6' }}>{f.help}</Form.Text>
              </Form.Group>
            ))}

            <Form.Group style={{ marginTop: 24, marginBottom: 18 }}>
              <Form.Check
                type="checkbox"
                id="abandonedCartEnabled"
                checked={!!form.abandonedCartEnabled}
                onChange={e => set('abandonedCartEnabled', e.target.checked)}
                label="Send an abandoned-cart reminder email"
              />
              <Form.Text style={{ color: '#9AA0A6' }}>
                Once a day, email clients who started a booking but didn’t finish (one reminder each).
                Requires your email (SMTP) settings to be configured.
              </Form.Text>
            </Form.Group>

            <Button variant="primary" disabled={saving} onClick={onSave}>
              {saving ? 'Saving…' : 'Save'}
            </Button>

            <SetupGuide />
          </>
        )}
      </Loader>
    </div>
  );
};

// In-page setup instructions (English) so any staff member can configure this without help.
const SetupGuide = () => {
  const wrap = { marginTop: 40, padding: 24, background: '#f8f9fa', border: '1px solid #e3e6ea', borderRadius: 12, lineHeight: 1.6, color: '#33373b' };
  const h3 = { fontSize: 18, fontWeight: 700, margin: '22px 0 8px' };
  const code = { background: '#eceff1', padding: '1px 6px', borderRadius: 4, fontFamily: 'monospace', fontSize: 13 };
  const note = { background: '#fff7e6', border: '1px solid #ffe0a3', borderRadius: 8, padding: '10px 14px', margin: '10px 0' };
  const link = { color: '#1a73e8' };
  return (
    <div style={wrap}>
      <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Setup guide (for staff)</h2>
      <p style={{ marginTop: 8 }}>
        This page only stores <b>tracking IDs (text)</b>. There are <b>no videos or images</b> here —
        ad creatives (videos, photos) live in your Meta/Google Ads accounts, not on this page. Paste
        the IDs below and click <b>Save</b>; each one is then loaded automatically on every page of the
        site, and a conversion event fires whenever a customer completes a booking.

      </p>

      <div style={note}>
        <b>Do this on the PRODUCTION site only.</b> The dev/staging site has its own database, so IDs
        entered there would track test traffic. Enter real IDs on the live admin.
      </div>

      <h3 style={h3}>1. Where to get each ID</h3>
      <ul>
        <li>
          <b>Google Analytics 4</b> (format <span style={code}>G-XXXXXXXXXX</span>):{' '}
          <a style={link} href="https://analytics.google.com" target="_blank" rel="noreferrer">analytics.google.com</a>{' '}
          → Admin → Data Streams → your Web stream → copy the <b>Measurement ID</b>.
        </li>
        <li>
          <b>Meta Pixel</b> (Facebook/Instagram, format <span style={code}>15–16 digits</span>):{' '}
          <a style={link} href="https://business.facebook.com/events_manager" target="_blank" rel="noreferrer">business.facebook.com</a>{' '}
          → Events Manager → your data source (Pixel) → copy the <b>Pixel ID</b>. This builds your
          retargeting audience.
        </li>
        <li>
          <b>Google Ads – Conversion ID</b> (format <span style={code}>AW-XXXXXXXXX</span>) and{' '}
          <b>Conversion Label</b>:{' '}
          <a style={link} href="https://ads.google.com" target="_blank" rel="noreferrer">ads.google.com</a>{' '}
          → Tools → Conversions → create a "Purchase" conversion action. The setup screen shows both the
          ID (<span style={code}>AW-…</span>) and the label.
        </li>
        <li>
          <b>Google Tag Manager</b> (format <span style={code}>GTM-XXXXXXX</span>) — optional/advanced:{' '}
          <a style={link} href="https://tagmanager.google.com" target="_blank" rel="noreferrer">tagmanager.google.com</a>{' '}
          → copy the container ID.
        </li>
      </ul>

      <h3 style={h3}>2. Choose ONE approach (so nothing is counted twice)</h3>
      <ul>
        <li><b>Simple (recommended):</b> fill in GA4 + Meta Pixel + Google Ads. Leave Tag Manager empty.</li>
        <li><b>Advanced:</b> fill in only Tag Manager and manage GA4/Meta/Ads inside GTM. In that case
          leave GA4 / Meta / Google Ads empty here.</li>
      </ul>

      <h3 style={h3}>3. Steps</h3>
      <ol>
        <li>Create the accounts (GA4, Meta Business/Pixel, Google Ads as needed).</li>
        <li>Copy each ID from those tools.</li>
        <li>Paste them into the fields above and click <b>Save</b>.</li>
        <li>Open the public site and confirm the tags load — use the free browser extensions{' '}
          <a style={link} href="https://chrome.google.com/webstore/detail/meta-pixel-helper/fdgfkebogiimcoedlicjlajpkdmockpc" target="_blank" rel="noreferrer">Meta Pixel Helper</a>{' '}
          and{' '}
          <a style={link} href="https://tagassistant.google.com" target="_blank" rel="noreferrer">Google Tag Assistant</a>.</li>
      </ol>

      <h3 style={h3}>4. Abandoned-cart reminder</h3>
      <p>
        Turn on the checkbox above to email customers who started a booking but didn’t finish (one
        reminder each, once a day). <b>This only works if the email (SMTP) settings are configured</b>
        {' '}in Admin → Site/Email settings. If SMTP is empty, the reminder is skipped automatically.
      </p>

      <h3 style={h3}>5. Where the ads and videos go</h3>
      <p>
        Once the Pixel/tags are saved here, build your campaigns in <b>Meta Ads Manager</b> and{' '}
        <b>Google Ads</b>: create a <b>retargeting campaign</b> that targets the audience the Pixel has
        collected (visitors who didn’t book), and upload your <b>videos/images</b> there. The website
        only "tags" visitors so your ads can find them again across the web.
      </p>
    </div>
  );
};

export default MarketingTracking;
